// externals
import { createSelector } from 'reselect';

// libraries
import { formatDollars } from '@makemydeal/dr-common-utils';

// types
import type {
    StateTree,
    TradeInIntelligenceAnalyticsRecord,
    TradeInIntelligenceDisplay,
    TradeInIntelligenceDisplayReason,
    TradeInIntelligenceMatchMethod,
    TradeInIntelligenceRange,
    TradeInIntelligenceResolution,
    TradeInIntelligenceRowState,
    TradeInIntelligenceState,
    TradeInValuationRecordServiceData
} from '@makemydeal/dr-dash-types';
import { TRADE_IN_INTELLIGENCE_LOOKBACK_DAYS, TRADE_IN_INTELLIGENCE_MAX_HOUSEHOLD_VEHICLES } from '@makemydeal/dr-dash-types';

// selectors
import { getAllTradeIns, isIncludedTradeIn } from './tradeIns';

// utils
import { isBlankTrade, isComparableTrade, matchVehicle, normalizeVehicleText } from '../utils/tradeInVehicleMatch';
import type { DealTradeVehicle } from '../utils/tradeInVehicleMatch';

import { initialState } from '../reducers/tradeInIntelligenceReducer';

const isKnownAmount = (value: number | null): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;

const hasText = (value: string | null): boolean => typeof value === 'string' && value.trim() !== '';

const isWithinHouseholdCutoff = (record: TradeInValuationRecordServiceData): boolean =>
    record.householdVehicleCount === null || record.householdVehicleCount <= TRADE_IN_INTELLIGENCE_MAX_HOUSEHOLD_VEHICLES;

const hasRange = (record: TradeInValuationRecordServiceData): boolean =>
    isKnownAmount(record.tradeValueLow) && isKnownAmount(record.tradeValueMid) && isKnownAmount(record.tradeValueHigh);

const isDescribable = (record: TradeInValuationRecordServiceData): boolean => hasText(record.make) && hasText(record.model);

const DAY_MS = 24 * 60 * 60 * 1000;

const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

const KBB_CONDITIONS: Record<string, string> = {
    excellent: 'Excellent',
    verygood: 'Very Good',
    good: 'Good',
    fair: 'Fair',
    poor: 'Poor'
};

export const toSeenAt = (presentedAt: string | null): string | null =>
    presentedAt && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(presentedAt) && !Number.isNaN(Date.parse(presentedAt))
        ? presentedAt
        : null;

const toPresentedAtMs = (seenAt: string): number => Date.parse(HAS_ZONE.test(seenAt) ? seenAt : `${seenAt}Z`);

type DatedRecord = { record: TradeInValuationRecordServiceData; seenAt: string; presentedAtMs: number };

const toDatedRecord = (record: TradeInValuationRecordServiceData): DatedRecord | null => {
    const seenAt = toSeenAt(record.presentedAt);
    return seenAt ? { record, seenAt, presentedAtMs: toPresentedAtMs(seenAt) } : null;
};

const isWithinLookback = (presentedAtMs: number, nowMs: number): boolean =>
    nowMs - presentedAtMs <= TRADE_IN_INTELLIGENCE_LOOKBACK_DAYS * DAY_MS;

const toModelKey = ({ year, make, model }: TradeInValuationRecordServiceData): string =>
    [year, make, model].map(normalizeVehicleText).join('|');

const isSameLookedUpVehicle = (left: TradeInValuationRecordServiceData, right: TradeInValuationRecordServiceData): boolean => {
    const leftVin = normalizeVehicleText(left.vin);
    const rightVin = normalizeVehicleText(right.vin);
    return leftVin && rightVin ? leftVin === rightVin : toModelKey(left) === toModelKey(right);
};

const keepMostRecentPerVehicle = (dated: DatedRecord[]): DatedRecord[] => {
    const latestPerVehicle: DatedRecord[] = [];
    dated.forEach((entry) => {
        const index = latestPerVehicle.findIndex((latest) => isSameLookedUpVehicle(latest.record, entry.record));
        if (index === -1) {
            latestPerVehicle.push(entry);
        } else if (entry.presentedAtMs > latestPerVehicle[index].presentedAtMs) {
            latestPerVehicle[index] = entry;
        }
    });
    return latestPerVehicle;
};

export const formatMileage = (mileage: number | null): string | null => {
    if (!isKnownAmount(mileage)) {
        return null;
    }
    return mileage < 1000 ? `${Math.round(mileage)} mi` : `${Math.round(mileage / 1000)}K mi`;
};

const toTitleCase = (text: string): string =>
    text
        .replace(/[_-]+/g, ' ')
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

export const formatCondition = (condition: string | null): string | null => {
    if (!hasText(condition)) {
        return null;
    }
    const text = condition as string;
    return KBB_CONDITIONS[text.toLowerCase().replace(/[^a-z]/g, '')] ?? toTitleCase(text);
};

const toEnteredInputs = (record: TradeInValuationRecordServiceData): string | null => {
    const mileage = formatMileage(record.mileage);
    const condition = formatCondition(record.condition);
    return mileage && condition ? `${mileage} · ${condition}` : null;
};

const toRange = (record: TradeInValuationRecordServiceData): TradeInIntelligenceRange => ({
    low: formatDollars(record.tradeValueLow),
    likely: formatDollars(record.tradeValueMid),
    high: formatDollars(record.tradeValueHigh)
});

const composeVehicle = (record: TradeInValuationRecordServiceData): string =>
    [record.year === null ? null : String(record.year), record.make, record.model]
        .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
        .map((part) => part.trim())
        .join(' ');

const byRankThenRecency = (left: DatedRecord, right: DatedRecord): number => {
    const leftRank = left.record.rankInGcid ?? Number.POSITIVE_INFINITY;
    const rightRank = right.record.rankInGcid ?? Number.POSITIVE_INFINITY;
    if (leftRank !== rightRank) {
        return leftRank - rightRank;
    }
    return right.presentedAtMs - left.presentedAtMs;
};

type TradeInIntelligenceRecordResolution =
    | { resolution: 'match' | 'headsUp'; entry: DatedRecord }
    | { resolution: 'mismatch' | 'nothing' };

type TradeInIntelligenceEvaluation = {
    resolved: TradeInIntelligenceRecordResolution;
    display: TradeInIntelligenceDisplay | null;
};

type TradeInIntelligenceMatch = { entry: DatedRecord; matchMethod: TradeInIntelligenceMatchMethod };

const findMatch = (candidates: DatedRecord[], trades: DealTradeVehicle[]): TradeInIntelligenceMatch | null => {
    for (const entry of candidates) {
        for (const trade of trades) {
            const matchMethod = matchVehicle(trade, entry.record);
            if (matchMethod) {
                return { entry, matchMethod };
            }
        }
    }
    return null;
};

const evaluateTradeInIntelligence = (
    slice: TradeInIntelligenceState,
    dealTrades: DealTradeVehicle[],
    nowMs: number = Date.now()
): TradeInIntelligenceEvaluation => {
    if (slice.status !== 'settled') {
        return { resolved: { resolution: 'nothing' }, display: null };
    }

    const inWindow = slice.records
        .filter((record) => isWithinHouseholdCutoff(record) && hasRange(record))
        .map(toDatedRecord)
        .filter((entry): entry is DatedRecord => entry !== null && isWithinLookback(entry.presentedAtMs, nowMs));
    const candidates = keepMostRecentPerVehicle(inWindow).sort(byRankThenRecency);
    const enteredTrades = dealTrades.filter((trade) => !isBlankTrade(trade));

    const toDisplay = (
        resolution: TradeInIntelligenceResolution,
        reason: TradeInIntelligenceDisplayReason | null,
        shown: DatedRecord | null = null,
        matchMethod: TradeInIntelligenceMatchMethod | null = null
    ): TradeInIntelligenceDisplay => ({
        resolution,
        reason,
        matchMethod,
        shownRank: shown?.record.rankInGcid ?? null,
        recordCount: slice.records.length,
        candidateCount: candidates.length,
        enteredTradeCount: enteredTrades.length
    });

    if (candidates.length === 0) {
        const reason = slice.records.length === 0 ? 'no_records' : 'no_usable_records';
        return { resolved: { resolution: 'nothing' }, display: toDisplay('nothing', reason) };
    }

    if (enteredTrades.length === 0) {
        const described = candidates.find((entry) => isDescribable(entry.record));
        return described
            ? { resolved: { resolution: 'headsUp', entry: described }, display: toDisplay('headsUp', null, described) }
            : { resolved: { resolution: 'nothing' }, display: toDisplay('nothing', 'not_describable') };
    }

    const matched = findMatch(candidates, enteredTrades);
    if (!matched) {
        const reason = enteredTrades.some(isComparableTrade) ? 'different_vehicle' : 'incomplete_trade';
        return { resolved: { resolution: 'mismatch' }, display: toDisplay('mismatch', reason) };
    }

    return {
        resolved: { resolution: 'match', entry: matched.entry },
        display: toDisplay('match', null, matched.entry, matched.matchMethod)
    };
};

export const resolveTradeInIntelligenceRecord = (
    slice: TradeInIntelligenceState,
    dealTrades: DealTradeVehicle[],
    nowMs: number = Date.now()
): TradeInIntelligenceRecordResolution => evaluateTradeInIntelligence(slice, dealTrades, nowMs).resolved;

const toTradeInIntelligenceRow = (resolved: TradeInIntelligenceRecordResolution): TradeInIntelligenceRowState => {
    switch (resolved.resolution) {
        case 'headsUp':
            return {
                resolution: 'headsUp',
                range: toRange(resolved.entry.record),
                seenAt: resolved.entry.seenAt,
                enteredInputs: toEnteredInputs(resolved.entry.record),
                vehicle: composeVehicle(resolved.entry.record)
            };
        case 'match':
            return {
                resolution: 'match',
                range: toRange(resolved.entry.record),
                seenAt: resolved.entry.seenAt,
                enteredInputs: toEnteredInputs(resolved.entry.record)
            };
        default:
            return { resolution: resolved.resolution };
    }
};

export const resolveTradeInIntelligenceRow = (
    slice: TradeInIntelligenceState,
    dealTrades: DealTradeVehicle[],
    nowMs: number = Date.now()
): TradeInIntelligenceRowState => toTradeInIntelligenceRow(resolveTradeInIntelligenceRecord(slice, dealTrades, nowMs));

const toTradeInIntelligenceAnalyticsRecord = (
    resolved: TradeInIntelligenceRecordResolution
): TradeInIntelligenceAnalyticsRecord | null => {
    if (!('entry' in resolved)) {
        return null;
    }
    const { vin, tradeValueLow, tradeValueMid, tradeValueHigh } = resolved.entry.record;
    return { vin, tradeValueLow, tradeValueMid, tradeValueHigh };
};

export const getTradeInIntelligence = (state: StateTree): TradeInIntelligenceState => state?.tradeInIntelligence ?? initialState;

export const getIsTradeInIntelligencePending = (state: StateTree): boolean => getTradeInIntelligence(state).status === 'pending';

export const getTradeInIntelligenceRequestedAtMs = (state: StateTree): number | null => getTradeInIntelligence(state).requestedAtMs;

export const getDealTradeVehicles = createSelector([getAllTradeIns], (allTradeIns): DealTradeVehicle[] =>
    allTradeIns.filter(isIncludedTradeIn).map(({ tradeIn }) => ({
        vin: tradeIn.vin,
        year: tradeIn.year,
        make: tradeIn.make,
        model: tradeIn.model,
        value: tradeIn.value
    }))
);

const getTradeInIntelligenceEvaluation = createSelector(
    [getTradeInIntelligence, getDealTradeVehicles],
    evaluateTradeInIntelligence
);

const getResolvedTradeInIntelligenceRecord = createSelector(
    [getTradeInIntelligenceEvaluation],
    (evaluation) => evaluation.resolved
);

export const getTradeInIntelligenceDisplay = createSelector([getTradeInIntelligenceEvaluation], (evaluation) => evaluation.display);

export const getTradeInIntelligenceRowState = createSelector([getResolvedTradeInIntelligenceRecord], toTradeInIntelligenceRow);

export const getTradeInIntelligenceAnalyticsRecord = createSelector(
    [getResolvedTradeInIntelligenceRecord],
    toTradeInIntelligenceAnalyticsRecord
);
